import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import {
  support,
  supportDate,
  supportKinds,
  type SupportKind,
  type SupportRequest,
} from "@/lib/support";
import {
  supportContext,
  SUPPORT_ERROR_EVENT,
  SUPPORT_OPEN_EVENT,
  SUPPORT_QUESTION_EVENT,
  type SupportError,
} from "@/lib/supportContext";
import { Button } from "./ui/button";
import { Dialog } from "./ui/design-system";
import { ReportProblemButton } from "./ReportProblemButton";
import "./Support.css";

export function SupportEntry() {
  const { user } = useAuth();
  const [draft, setDraft] = useState<{
    context: ReturnType<typeof supportContext>;
    error?: SupportError;
    kind?: SupportKind;
  } | null>(null);
  const [error, setError] = useState<SupportError | null>(null);
  useEffect(() => {
    const show = (event: Event) => {
      const selected = (event as CustomEvent<SupportError | undefined>).detail;
      setDraft({ context: supportContext(), error: selected });
    };
    const question = () => setDraft({ context: supportContext(), kind: "ne_znam_kako" });
    const failed = (event: Event) => {
      const failure = (event as CustomEvent<SupportError>).detail;
      setError(
        failure.status === null ||
          (failure.status >= 500 && failure.status < 600)
          ? failure
          : null,
      );
    };
    window.addEventListener(SUPPORT_OPEN_EVENT, show);
    window.addEventListener(SUPPORT_QUESTION_EVENT, question);
    window.addEventListener(SUPPORT_ERROR_EVENT, failed);
    return () => {
      window.removeEventListener(SUPPORT_OPEN_EVENT, show);
      window.removeEventListener(SUPPORT_QUESTION_EVENT, question);
      window.removeEventListener(SUPPORT_ERROR_EVENT, failed);
    };
  }, []);
  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(
      () => setError(null),
      Math.max(0, 5 * 60_000 - (Date.now() - Date.parse(error.occurred_at))),
    );
    return () => window.clearTimeout(timer);
  }, [error]);
  if (!user) return null;
  return (
    <>
      <Button
        className="support-topbar"
        variant="secondary"
        onClick={() => setDraft({ context: supportContext() })}
      >
        💬 Pitaj
      </Button>
      {error && !draft && (
        <div className="support-floating-error">
          <div className="support-error">
            <p>{error.message}</p>
            {!error.message.includes("Mario je automatski obaviješten") && (
              <ReportProblemButton />
            )}
            <Button
              variant="tertiary"
              aria-label="Zatvori obavijest o grešci"
              onClick={() => setError(null)}
            >
              ×
            </Button>
          </div>
        </div>
      )}
      {draft && (
        <SupportForm
          context={draft.context}
          initialError={draft.error}
          initialKind={draft.kind}
          onClose={() => setDraft(null)}
          onSent={() => {
            setDraft(null);
            setError(null);
          }}
        />
      )}
    </>
  );
}
export function SupportForm({
  context,
  initialError,
  initialKind,
  onClose,
  onSent,
}: {
  context: ReturnType<typeof supportContext>;
  initialError?: SupportError;
  initialKind?: SupportKind;
  onClose: () => void;
  onSent: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kind, setKind] = useState<SupportKind | null>(
    initialError ? "nesto_ne_radi" : initialKind ?? null,
  );
  const [text, setText] = useState(initialError?.message ?? "");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<SupportRequest | null>(null);
  const [openedAt] = useState(() => new Date().toISOString());
  const lastError = initialError ?? context.last_error;
  function addFiles(selected: File[]) {
    if (files.length + selected.length > 3) {
      setError("Dodajte najviše 3 fotografije.");
      return;
    }
    if (
      selected.some(
        (file) =>
          !["image/jpeg", "image/png"].includes(file.type) ||
          file.size > 15 * 1024 * 1024,
      )
    ) {
      setError("Odaberite JPEG ili PNG fotografije, do 15 MB po fotografiji.");
      return;
    }
    setFiles((previous) => [...previous, ...selected]);
    setError("");
  }
  async function submit() {
    if (!kind || text.trim().length < 3 || busy) return;
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.set(
        "podaci",
        JSON.stringify({
          ...context,
          kind,
          text: text.trim(),
          last_error: lastError,
        }),
      );
      for (const file of files) body.append("fotke", file, file.name);
      setSent(await support.create(body));
    } catch (cause) {
      setError(errorMessage(cause, "Prijava nije poslana. Pokušajte ponovno."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={sent ? "Prijava je poslana" : "Prijavi problem ili pitaj"}
      className="support-modal"
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      {sent ? (
        <div className="support-stack">
          <p className="support-success" role="status">
            ✓ Poslano. Odgovor stiže ovdje i na mail.
          </p>
          {sent.mail_delivered === false && (
            <p role="status">
              Prijava je spremljena. Slanje maila čeka ponovni pokušaj.
            </p>
          )}
          <div className="support-card">
            <strong>Prijava #{sent.number}</strong>
            <p className="support-text">{sent.text}</p>
          </div>
          <Button
            onClick={() => {
              onSent();
              navigate("/pomoc", { state: { submitted: sent.id } });
            }}
          >
            Moje prijave
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Natrag na posao
          </Button>
        </div>
      ) : (
        <form
          className="support-stack support-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <fieldset disabled={busy} className="support-stack">
            <legend className="mb-2 text-sm">1. Što se događa?</legend>
            <div className="support-stack" style={{ gap: 8 }}>
              {supportKinds.map((item) => (
                <button
                  type="button"
                  className="support-kind"
                  key={item.value}
                  aria-pressed={kind === item.value}
                  onClick={() => setKind(item.value)}
                >
                  <span className="support-kind__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span>
                    <strong>{item.label}</strong>
                    <small>{item.hint}</small>
                  </span>
                </button>
              ))}
            </div>
            {kind && (
              <>
                <label>
                  2. Napiši ukratko
                  <textarea
                    required
                    minLength={3}
                    maxLength={10000}
                    placeholder="Npr. ne vidim gumb za primitak"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                  />
                </label>
                <p className="text-sm text-muted-foreground">
                  Možete koristiti diktat tipkovnice na svom mobitelu.
                </p>
                <div className="support-row">
                  <label className="support-upload">
                    📷 Fotografiraj
                    <input
                      aria-label="Fotografiraj problem"
                      type="file"
                      accept="image/jpeg,image/png"
                      capture="environment"
                      multiple
                      onChange={(event) => {
                        addFiles(Array.from(event.target.files ?? []));
                        event.target.value = "";
                      }}
                    />
                  </label>
                  <label className="support-upload">
                    Dodaj iz galerije
                    <input
                      aria-label="Dodaj fotografije iz galerije"
                      type="file"
                      accept="image/jpeg,image/png"
                      multiple
                      onChange={(event) => {
                        addFiles(Array.from(event.target.files ?? []));
                        event.target.value = "";
                      }}
                    />
                  </label>
                </div>
                {files.length > 0 && (
                  <ul className="space-y-2">
                    {files.map((file, index) => (
                      <li className="support-row" key={`${file.name}-${index}`}>
                        <span className="break-all">{file.name}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="tertiary"
                          aria-label={`Ukloni ${file.name}`}
                          onClick={() =>
                            setFiles((previous) =>
                              previous.filter(
                                (_, position) => position !== index,
                              ),
                            )
                          }
                        >
                          Ukloni
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="support-context">
                  <strong>Aplikacija sama šalje</strong>
                  <span>
                    {user?.full_name} ·{" "}
                    {user?.roles.map((role) => role.name_hr).join(", ")}
                  </span>
                  <span>Ekran: {context.screen_name}</span>
                  <span>{context.url}</span>
                  <span>
                    {context.user_agent} · {supportDate(openedAt)} (Zagreb)
                  </span>
                  {lastError && (
                    <span>
                      Greška: {lastError.message} ·{" "}
                      {lastError.status ?? "bez odgovora"} ·{" "}
                      {lastError.endpoint}
                      {lastError.request_id ? ` · ${lastError.request_id}` : ""}
                    </span>
                  )}
                </div>
                <Button type="submit" disabled={busy || text.trim().length < 3}>
                  {busy ? "Slanje…" : "Pošalji"}
                </Button>
              </>
            )}
          </fieldset>
          {error && <p role="alert">{error}</p>}
          <Link to="/pomoc" onClick={onClose}>
            Moje prijave
          </Link>
        </form>
      )}
    </Dialog>
  );
}
